import * as React from 'react'

import styled, { css } from 'styled-components'
import { useInView } from 'react-intersection-observer'

import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'

import { Appear } from './Appear'
import { Brush } from './Brush'
import { BrushTitleAnimated } from '@bob/components'
import { SITE_URL } from '@bob/config'

export type WhyProps = React.HTMLAttributes<HTMLDivElement>

function OurLabelComponent({ ...rest }: WhyProps): React.ReactElement {
  const inViewOptions = {
    threshold: 0.0,
    rootMargin: `0px 0px -40% 0px`,
    initialInView: false,
    triggerOnce: true,
  }

  const [$part1, inViewPart1] = useInView(inViewOptions)

  const titles = [
    {
      inView: inViewPart1,
      text: 'Nos critères : des produits bons pour la planète, bons pour les humains',
      tag: 'h2',
      Component: S.heading.Title2,
      brushes: [
        {
          wordIndex: 5,
          comp: Brush,
          props: { brushId: 'yellow' },
        },
        {
          wordIndex: 8,
          comp: Brush,
          props: { brushId: 'green' },
        },
        {
          wordIndex: 12,
          comp: Brush,
          props: { brushId: 'pink' },
        },
      ],
    },
  ]

  return (
    <Section {...rest}>
      <WhyPart1 ref={$part1} inView={inViewPart1}>
        <BrushTitleAnimated {...titles[0]} />
        <ImageText inView={inViewPart1}>
          <S.copy.Text5>
            <Appear isVisible={inViewPart1} delay={1}>
              <p>
                Les produits des marques signataires cochent toujours au moins 1 critère social et 1 critère
                environnemental parmi nos 13 critères fondamentaux.
              </p>
              <p>
                Certaines marques ayant une gamme large peuvent avoir une partie seulement de leurs produits qui
                correspond à ce double critère social-environnemental. Dans ce cas, la marque démontre des engagements
                clairs pour basculer une majorité de ses produits sur le double-critère.
              </p>
              <p>
                Pour être complets, précisons qu’on ne cherche pas les labels à tout prix : on ne veut pas pénaliser des
                entreprises qui se donnent à fond mais qui n’ont pas les ressources pour financer une labellisation. Si,
                sur un critère donné, une marque n’a pas de labellisation et si un doute subsiste, nous lui demandons
                des éléments prouvant qu’elle respecte bien le critère en question.
              </p>
            </Appear>
          </S.copy.Text5>
          <ImageTextPictureContainer>
            <Appear isVisible={inViewPart1} delay={1.2}>
              <ds.Picture src={SITE_URL + '/assets/rasterized/manifesto/manifeste-01.png'} width="630" height="575" />
            </Appear>
            <Appear isVisible={inViewPart1} delay={1.3} hasTranslate={false}>
              <ds.stickers.others.UsefulKnowledge />
            </Appear>
          </ImageTextPictureContainer>
        </ImageText>
      </WhyPart1>
    </Section>
  )
}

export const OurLabel = styled(OurLabelComponent)`
  ${S.copy.Text5} {
    font-size: 18px; // HACK: Irregular font manipulation
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${S.copy.Text5} {
      font-size: 24px; // HACK: Irregular font manipulation
    }
  }
`

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding-top: 35px;
  padding-bottom: 92px;

  ${S.heading.Title2} {
    text-align: center;
    margin-bottom: 70px;
  }

  ${ds.stickers.Sticker} {
    position: absolute;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    padding-top: 60px;

    ${S.heading.Title2} {
      margin: 0 ${(110 / 1280) * 100}% ${(114 / 1280) * 100}%;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    padding-top: 110px;

    ${ds.stickers.Sticker} {
      width: 182px;
      height: 182px;
    }
  }
`

const WhyPart1 = styled(S.container.Base)<{ inView: boolean }>`
  ${BrushTitleAnimated} {
    margin-right: 0;
    margin-left: 0;
  }

  ${ds.stickers.Sticker} {
    left: 0;
    bottom: 0;
    transform: translate(${(60 / 182) * 100}%, ${(50 / 182) * 100}%);
  }

  ${S.copy.Text5} {
    padding-top: 53px;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${ds.stickers.Sticker} {
      transform: translate(${(80 / 182) * 100}%, ${(100 / 182) * 100}%);
    }

    ${S.copy.Text5} {
      padding-top: 0;
    }
  }
`

const ImageTextPictureContainer = styled.div`
  position: relative;
  align-self: flex-start;
  grid-row: 1 / span 1;

  ${ds.Picture} {
    width: ${(395 / 335) * 100}%;
    margin-left: 15px;
    border-radius: 12px;
  }

  &:first-child {
    ${ds.Picture} {
      margin-right: 0;
      margin-left: -78px;
    }
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-row: auto;

    ${ds.Picture} {
      width: ${(630 / 515) * 100}%;
      margin-right: -${(630 / 515) * 100 - 100}%;
    }

    &:first-child {
      grid-column: 1 / span 5;

      ${ds.Picture} {
        margin-right: 0;
        margin-left: -${(630 / 515) * 100 - 100}%;
      }
    }

    &:last-child {
      grid-column: 8 / -1;
    }
  }
`

const ImageText = styled.div<{ inView?: boolean }>`
  display: grid;
  gap: 20px;

  ${S.copy.Text5} {
    p {
      margin: 1.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  ${S.copy.Text5},
  ${ImageTextPictureContainer} {
    opacity: 0;
    transform: translateY(30px);
    transition-delay: 100ms;
    transition-duration: 350ms;
    transition-property: opacity;
    transition-timing-function: ease-out;

    ${({ inView }) =>
      inView &&
      css`
        transform: translateY(0);
        opacity: 1;
      `}
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-template-columns: repeat(12, 1fr);

    ${S.copy.Text5} {
      &:first-child {
        grid-column: 1 / span 6;
      }

      &:last-child {
        grid-column: 7 / span 6;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    ${S.copy.Text5} {
      &:first-child {
        grid-column: 2 / span 5;
      }

      &:last-child {
        grid-column-end: span 5;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    gap: 36px;
  }
`
